import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  data: any;
  is_read: boolean;
  created_at: string;
}

interface NotificationPreferences {
  id: string;
  user_id: string;
  likes_enabled: boolean;
  pops_enabled: boolean;
  matches_enabled: boolean;
  messages_enabled: boolean;
  location_joins_enabled: boolean;
  favorites_enabled: boolean;
  portal_enabled: boolean;
  push_token: string | null;
}

export const useNotifications = () => {
  const { user } = useAuthContext();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const pendingDeletesRef = useRef<Map<string, { notification: Notification; timeoutId: NodeJS.Timeout }>>(new Map());

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (!error && data) {
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.is_read).length);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscribe to new notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;

    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);

    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, [user]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, [user]);

  const deleteNotification = useCallback((notificationId: string): { undo: () => void } => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || !user) return { undo: () => {} };

    // Optimistically remove from UI
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    if (!notification.is_read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    // Set up delayed actual deletion
    const timeoutId = setTimeout(async () => {
      pendingDeletesRef.current.delete(notificationId);
      await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);
    }, 5000);

    pendingDeletesRef.current.set(notificationId, { notification, timeoutId });

    // Return undo function
    const undo = () => {
      const pending = pendingDeletesRef.current.get(notificationId);
      if (pending) {
        clearTimeout(pending.timeoutId);
        pendingDeletesRef.current.delete(notificationId);
        
        // Restore to UI
        setNotifications((prev) => {
          const restored = [...prev, pending.notification].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          return restored;
        });
        if (!pending.notification.is_read) {
          setUnreadCount((prev) => prev + 1);
        }
      }
    };

    return { undo };
  }, [user, notifications]);

  const clearAllRead = useCallback(async () => {
    if (!user) return;

    // Cancel any pending deletes for read notifications
    pendingDeletesRef.current.forEach((pending, id) => {
      if (pending.notification.is_read) {
        clearTimeout(pending.timeoutId);
        pendingDeletesRef.current.delete(id);
      }
    });

    await supabase
      .from("notifications")
      .delete()
      .eq("user_id", user.id)
      .eq("is_read", true);

    setNotifications((prev) => prev.filter((n) => !n.is_read));
  }, [user]);

  return { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    clearAllRead,
    refetch: fetchNotifications 
  };
};

export const useNotificationPreferences = () => {
  const { user } = useAuthContext();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPreferences = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!error) {
      setPreferences(data);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreferences = useCallback(async (updates: Partial<NotificationPreferences>) => {
    if (!user) return { error: new Error("Not authenticated") };

    if (preferences) {
      const { error } = await supabase
        .from("notification_preferences")
        .update(updates)
        .eq("user_id", user.id);

      if (!error) {
        setPreferences((prev) => prev ? { ...prev, ...updates } : null);
      }
      return { error };
    } else {
      const { data, error } = await supabase
        .from("notification_preferences")
        .insert({ user_id: user.id, ...updates })
        .select()
        .single();

      if (!error && data) {
        setPreferences(data);
      }
      return { error };
    }
  }, [user, preferences]);

  return { preferences, loading, updatePreferences, refetch: fetchPreferences };
};
